import React, { useCallback, useEffect, useState } from "react";
import {
  createQuesero,
  deleteQuesero,
  getQueseros,
  updateQuesero,
} from "../utils/api.js";
import { Button, Card, Input, Badge } from "../components/legacy-ui.js";
import { AppPageHeader, AppTable } from "../components/app-ui.js";
import { Plus, Pencil, Trash2, Search, CookingPot } from "lucide-react";
import { useAuth } from "../context/AuthContext.js";
import { Helmet } from "react-helmet";
import { companyPageTitle } from "../utils/company.js";
import toast from "react-hot-toast";
import { LoadingState } from "../components/motion.js";

const permissionSummary = (quesero) => {
  const perms = quesero.permissions || {};
  const labels = [];
  if (perms.manageMilk) labels.push("Leche");
  if (perms.manageCheese) labels.push("Quesos");
  return labels.length ? labels.join(" · ") : "Sin permisos";
};

const emptyPermissions = {
  manageMilk: false,
  manageCheese: false,
};

const PERMISSION_OPTIONS = [
  {
    key: "manageMilk",
    label: "Gestión de la leche",
    hint: "Anota litros, descuentos y viajes de cada proveedor. No ve precios ni abonos.",
  },
  {
    key: "manageCheese",
    label: "Gestión de quesos",
    hint: "Registra las producciones de queso de la semana.",
  },
];

const emptyForm = {
  username: "",
  fullName: "",
  password: "",
  isActive: true,
  permissions: { ...emptyPermissions },
};

const QueserosModule = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchRows = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await getQueseros({
        search: debouncedSearch || undefined,
        limit: 100,
      });
      setRows(res.data.data || []);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.error || "No se pudieron cargar los queseros", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  }, [user, debouncedSearch]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormData(emptyForm);
  };

  const handleEdit = (row) => {
    setEditing(row);
    setFormData({
      username: row.username || "",
      fullName: row.fullName || "",
      password: "",
      isActive: row.isActive !== false,
      permissions: { ...emptyPermissions, ...(row.permissions || {}) },
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este quesero?")) return;
    try {
      await deleteQuesero(id);
      toast.success("Quesero eliminado", { position: "top-center" });
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar", { position: "top-center" });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.username.trim()) {
      toast.error("El usuario es obligatorio", { position: "top-center" });
      return;
    }
    if (!editing && !formData.password) {
      toast.error("La contraseña es obligatoria", { position: "top-center" });
      return;
    }
    if (formData.password && formData.password.length < 4) {
      toast.error("La contraseña debe tener al menos 4 caracteres", { position: "top-center" });
      return;
    }

    const payload = {
      username: formData.username.trim(),
      fullName: formData.fullName.trim(),
      isActive: Boolean(formData.isActive),
      permissions: {
        manageMilk: Boolean(formData.permissions.manageMilk),
        manageCheese: Boolean(formData.permissions.manageCheese),
      },
    };
    if (formData.password) payload.password = formData.password;

    setSaving(true);
    try {
      if (editing) {
        await updateQuesero(editing._id, payload);
        toast.success("Quesero actualizado", { position: "top-center" });
      } else {
        await createQuesero(payload);
        toast.success("Quesero creado", { position: "top-center" });
      }
      resetForm();
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar", { position: "top-center" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Queseros")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Queseros"
            subtitle="Cuentas para cargar leche y quesos, sin ver precios de proveedores"
            actions={
              <Button
                onClick={() => {
                  setEditing(null);
                  setFormData(emptyForm);
                  setShowForm(true);
                }}
              >
                <Plus size={16} /> Nuevo
              </Button>
            }
          />
          <div className="relative mt-4 mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por nombre o usuario..."
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm placeholder-gray-400"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
        </div>

        <div className="app-content">
          {showForm && (
            <Card className="p-4 mb-4 border border-indigo-100 bg-indigo-50/40">
              <h3 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <CookingPot size={16} />
                {editing ? "Editar quesero" : "Nuevo quesero"}
              </h3>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Nombre completo"
                  value={formData.fullName}
                  onChange={(event) => setFormData({ ...formData, fullName: event.target.value })}
                />
                <Input
                  label="Usuario *"
                  required
                  value={formData.username}
                  onChange={(event) => setFormData({ ...formData, username: event.target.value })}
                />
                <Input
                  label={editing ? "Nueva contraseña" : "Contraseña *"}
                  type="password"
                  value={formData.password}
                  onChange={(event) => setFormData({ ...formData, password: event.target.value })}
                />
                <label className="flex items-center gap-2 text-sm text-gray-700 md:mt-6">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(event) => setFormData({ ...formData, isActive: event.target.checked })}
                  />
                  Activo
                </label>
                <div className="md:col-span-2 space-y-2">
                  {PERMISSION_OPTIONS.map((option) => (
                    <label key={option.key} className="flex items-start gap-2 text-sm text-gray-800">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={Boolean(formData.permissions[option.key])}
                        onChange={(event) =>
                          setFormData({
                            ...formData,
                            permissions: {
                              ...formData.permissions,
                              [option.key]: event.target.checked,
                            },
                          })
                        }
                      />
                      <span>
                        <span className="font-medium">{option.label}</span>
                        <span className="block text-xs text-gray-500">{option.hint}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <div className="md:col-span-2 flex justify-end gap-2">
                  <Button type="button" variant="secondary" onClick={resetForm}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? "Guardando…" : "Guardar"}
                  </Button>
                </div>
              </form>
            </Card>
          )}

          {loading ? (
            <LoadingState label="Cargando queseros" />
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {rows.map((row) => (
                  <Card key={row._id} className="p-4">
                    <div className="flex justify-between items-start gap-2">
                      <div>
                        <h3 className="font-bold text-gray-900">{row.fullName || row.username}</h3>
                        <p className="text-sm text-gray-500">@{row.username}</p>
                        <p className="text-xs text-gray-500 mt-1">{permissionSummary(row)}</p>
                      </div>
                      <Badge type={row.isActive !== false ? "success" : "info"}>
                        {row.isActive !== false ? "Activo" : "Inactivo"}
                      </Badge>
                    </div>
                    <div className="flex gap-2 justify-end mt-3">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
                        <Pencil size={16} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(row._id)}>
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </Card>
                ))}
                {rows.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">No hay queseros registrados</p>
                )}
              </div>
              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "name", title: "Nombre" },
                    { key: "username", title: "Usuario" },
                    { key: "permissions", title: "Permisos" },
                    { key: "status", title: "Estado", align: "center" },
                    { key: "actions", title: "Acciones", align: "center" },
                  ]}
                >
                  {rows.map((row) => (
                    <tr key={row._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-3.5 text-sm font-medium text-gray-900">
                        {row.fullName || "—"}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">{row.username}</td>
                      <td className="px-6 py-3.5 text-sm text-gray-600">{permissionSummary(row)}</td>
                      <td className="px-6 py-3.5 text-center">
                        <Badge type={row.isActive !== false ? "success" : "info"}>
                          {row.isActive !== false ? "Activo" : "Inactivo"}
                        </Badge>
                      </td>
                      <td className="px-6 py-3.5 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(row)}>
                            <Pencil size={16} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(row._id)}>
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </AppTable>
                {rows.length === 0 && (
                  <p className="text-sm text-gray-500 text-center py-8">No hay queseros registrados</p>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default QueserosModule;
